import * as Actions from '../actions/deadlineRuleActions';
import DeadlineRule from '../../entities/deadlineRule';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type DeadlineRuleAction = { 
    type: string; 
    deadlineRule: DeadlineRule | null; 
    deadlineRuleId: string | DeadlineRule; 
    partial: boolean; 
    lastFetch: number; 
    deadlineRules: DeadlineRule[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<DeadlineRule> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {DeadlineRule[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<DeadlineRule> | null, firmId: string, userId: string, updates?: DeadlineRule[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'deadlineRule', 'deadlineRules', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<DeadlineRule>} data
 * @param {DeadlineRule[]} [updates]
 * @returns {ArrayMap<DeadlineRule>}
 */
function updateStorage(data: ArrayMap<DeadlineRule>, updates?: DeadlineRule[]): ArrayMap<DeadlineRule> {
	if (logging) console.debug('⮂ deadlineRuleReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {DeadlineRule[]} [updates]
 * @returns {ArrayMap<DeadlineRule>}
 */
function updateStorageNoReturn(updates?: DeadlineRule[]): void {
	if (logging) console.debug('⮂ deadlineRuleReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {DeadlineRule[]} [data]
 * @returns {ArrayMap<DeadlineRule>}
 */
function createState(data?: DeadlineRule[] | ArrayMap<DeadlineRule>): ArrayMap<DeadlineRule> {
	if (!data) return ArrayMapRegistry.clone<DeadlineRule>('deadlineRule', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<DeadlineRule>('deadlineRule', data.map(e => e));
    else return ArrayMapRegistry.clone<DeadlineRule>('deadlineRule', data);
}

function deadlineRules(state: ArrayMap<DeadlineRule>, action: DeadlineRuleAction): ArrayMap<DeadlineRule> {
	if (!state) state = createState();

	let st: ArrayMap<DeadlineRule>;

	switch (action.type) {
		// action.deadlineRule - the new value of deadlineRule with id action.id
		// action.deadlineRuleId - id of the target, used if new value is null
		case Actions.DEADLINERULE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ deadlineRuleReducer: FETCH_ONE_SUCCESS', action);

			if (action.deadlineRule === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.deadlineRuleId)) {
				st = state.clone();
				st.set(action.deadlineRule);
				// state.clear();

				updateStorageNoReturn([action.deadlineRule]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.deadlineRules
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.deadlineRules
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.deadlineRules
			 !partial, !stateEmpty, fetchEmpty	-> D return deadlineRules in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge deadlineRules in state newer than those in action.deadlineRules
		 */
		case Actions.DEADLINERULE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.deadlineRules.length === 0;
			const fetched = action.deadlineRules.map(deadlineRule => new DeadlineRule({...deadlineRule, persisted: true}));

			if (logging) console.debug(`⮂ deadlineRuleReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} deadlineRules with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ deadlineRuleReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ deadlineRuleReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<DeadlineRule> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: DeadlineRule[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(deadlineRule => {
					const current = st.get(deadlineRule.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && deadlineRule.status === 'D') {
							// Remove it from state
							toUpdate.push(deadlineRule);
							st.delete(deadlineRule);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (deadlineRule.addedOn > current.addedOn) {
								toUpdate.push(deadlineRule);
								st.set(deadlineRule);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (deadlineRule.status !== 'D') {
							toUpdate.push(deadlineRule);
							st.set(deadlineRule);
						}
						else if (logging) console.debug('⮂ deadlineRuleReducer: partialMerge: skipping deleted deadlineRule not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ deadlineRuleReducer: empty: deadlineRules', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ deadlineRuleReducer: fullMerge: deadlineRules', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localDeadlineRule => {
                    // If this deadlineRule has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localDeadlineRule.persisted) {
                        const fetchedDeadlineRule = st.get(localDeadlineRule.id);
                        if (!fetchedDeadlineRule || localDeadlineRule.addedOn > fetchedDeadlineRule.addedOn) st.set(localDeadlineRule);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.DEADLINERULE_FETCH_ONE_FAILURE:
			console.error('DEADLINERULE_FETCH_ONE_FAILURE', action.deadlineRuleId, action.err);

			// No update to storage required
			return state;

		case Actions.DEADLINERULE_FETCH_FAILURE:
			console.error('DEADLINERULE_FETCH_FAILURE', action.deadlineRuleId, action.err);

			// No update to storage required
			return state;

		case Actions.DEADLINERULE_RESTORE: {
		    let deadlineRule = new DeadlineRule({...action.deadlineRule});
            st = state.clone();

            const existing = st.get(deadlineRule.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (deadlineRule.addedOn >= existing.addedOn) {
                    st.set(deadlineRule);
                    return updateStorage(st, [deadlineRule]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (deadlineRule.status === 'D') return state;

                st.set(deadlineRule);
                return updateStorage(st, [deadlineRule]);
            }
        }
            
		case Actions.DEADLINERULE_SAVE: {
		    // console.debug('⮂ DEADLINERULE_SAVE reducer', action.deadlineRule?.id);
		    
			// clone the deadlineRule
			let deadlineRule = new DeadlineRule({...action.deadlineRule});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new deadlineRule in the state
			// If this is a create, add to the queue
			const _deadlineRule = st.get(deadlineRule.id);

			if (!_deadlineRule) st.set(deadlineRule);
			else {
				if (deadlineRule.addedOn >= _deadlineRule.addedOn) st.set(deadlineRule);
			}

			return updateStorage(st, [deadlineRule]);
		}

		case Actions.DEADLINERULE_SAVE_MANY: {
		    if (logging) console.debug('⮂ DEADLINERULE_SAVE_MANY reducer', action.deadlineRules.map(deadlineRule => deadlineRule.id).join(','));
		    
			// clone the deadlineRules
			let toProcess = action.deadlineRules.map(deadlineRule => new DeadlineRule(deadlineRule));

			st = state.clone();
			// state.clear();

			const updates: DeadlineRule[] = [];

			toProcess.forEach(deadlineRule => {
				// Add or update the new deadlineRule in the state
				// If this is a create, add to the queue
				const existing = st.get(deadlineRule.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (deadlineRule.status !== 'D') {
						st.push(deadlineRule);
						updates.push(deadlineRule);
					}
					else if (logging) console.debug('⮂ DEADLINERULE_SAVE_MANY: skipping deleted deadlineRule not in state');
				}
				else {
					if (deadlineRule.addedOn >= existing.addedOn) {
						st.set(deadlineRule);
						updates.push(deadlineRule);
					}
					else if (logging) console.debug('⮂ Skipping, existing deadlineRule had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DEADLINERULE_REMOVE: {
			// clone the deadlineRule
			const deadlineRule = new DeadlineRule({...action.deadlineRule});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(deadlineRule);

			return updateStorage(st, [deadlineRule]);
		}

		case Actions.DEADLINERULE_RESTORE_FAILURE:
		case Actions.DEADLINERULE_SAVE_FAILURE: {
		    console.warn('DEADLINERULE_SAVE_FAILURE reducer', action.deadlineRule?.id);
		    if (!action.deadlineRule?.id) {
				console.warn('DEADLINERULE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const deadlineRule = ensure(DeadlineRule, action.deadlineRule);
			if (!deadlineRule) {
				console.error('Could not find deadlineRule after call to save');
				return state;
			}

			st = state.clone();

			const _deadlineRuleRef = state.get(deadlineRule.id);

			if (_deadlineRuleRef && (_deadlineRuleRef.addedOn === deadlineRule.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _deadlineRule = new DeadlineRule({..._deadlineRuleRef});
				_deadlineRule.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_deadlineRule.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('deadlineRule', _deadlineRule).then(r => {
						if (r.isErr()) console.error(`⮂ deadlineRuleReducer: failed to save deadlineRule with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ deadlineRuleReducer: saved deadlineRule with fatalError info');
					}).catch(err => console.error(`⮂ deadlineRuleReducer: failed to save deadlineRule with fatalError info: ${err.message}`));

				st.set(_deadlineRule);
			}

			return updateStorage(st);
		}

		case Actions.DEADLINERULE_SAVE_MANY_FAILURE: {
		    console.warn('DEADLINERULE_SAVE_MANY_FAILURE reducer', action.deadlineRules.map(deadlineRule => deadlineRule.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.DEADLINERULE_RESTORE_SUCCESS:
		case Actions.DEADLINERULE_SAVE_SUCCESS: {
			if (!action.deadlineRule?.id) {
				console.warn('DEADLINERULE_SAVE_SUCCESS reducer');
                return state;
			}

			const deadlineRuleRef = state.get(action.deadlineRule?.id);
			if (!deadlineRuleRef) {
				console.warn('Could not find deadlineRule after call to save');
                return state;
			}

			st = state.clone();

			if (action.deadlineRule.addedOn >= deadlineRuleRef.addedOn) {
				st.set(action.deadlineRule);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const deadlineRule = new DeadlineRule({...deadlineRuleRef, persisted: true});
				st.set(deadlineRule);
			}

			return updateStorage(st, [action.deadlineRule]);
		}

		case Actions.DEADLINERULE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ DEADLINERULE_SAVE_MANY_SUCCESS reducer', action.deadlineRules.map(deadlineRule => deadlineRule.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const deadlineRules: DeadlineRule[] = [];

			action.deadlineRules.forEach(e => {
				const deadlineRuleRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!deadlineRuleRef) console.warn('Could not find deadlineRule after call to save');
				else if (e.addedOn >= deadlineRuleRef.addedOn) {
				    st.set(e);
				    deadlineRules.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const deadlineRule = new DeadlineRule({...deadlineRuleRef, persisted: true});
					st.set(deadlineRule);
					deadlineRules.push(deadlineRule);
				}
			});

			return updateStorage(st, deadlineRules);
		}

		case Actions.DEADLINERULE_REMOVE_FAILURE: {
		    console.warn('DEADLINERULE_REMOVE_FAILURE action', action.deadlineRule?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.DEADLINERULE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.DEADLINERULE_RESTORE_MANY: {
			if (logging) console.debug('⮂ DEADLINERULE_RESTORE_MANY reducer', action.deadlineRules.map(deadlineRule => deadlineRule.id).join(','));

			const toProcess = action.deadlineRules.map(deadlineRule => new DeadlineRule({...deadlineRule}));
			st = state.clone();

			const updates: DeadlineRule[] = [];

			toProcess.forEach(deadlineRule => {
				const existing = st.get(deadlineRule.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (deadlineRule.addedOn >= existing.addedOn) {
						st.set(deadlineRule);
						updates.push(deadlineRule);
					}
					else if (logging) console.debug('⮂ DEADLINERULE_RESTORE_MANY: skipping older version of deadlineRule');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (deadlineRule.status !== 'D') {
						st.set(deadlineRule);
						updates.push(deadlineRule);
					}
					else if (logging) console.debug('⮂ DEADLINERULE_RESTORE_MANY: skipping deleted deadlineRule not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DEADLINERULE_REMOVE_MANY: {
			if (logging) console.debug('⮂ DEADLINERULE_REMOVE_MANY reducer', action.deadlineRules.map(deadlineRule => deadlineRule.id).join(','));

			const toProcess = action.deadlineRules.map(deadlineRule => new DeadlineRule({...deadlineRule}));
			st = state.clone();

			toProcess.forEach(deadlineRule => {
				st.delete(deadlineRule);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default deadlineRules;